import { supabase } from '../supabaseClient';

export interface ContactMessageData {
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  clientCompany?: string;
  subject: string;
  message: string;
  serviceTitle: string;
  serviceId: string;
  executorEmail: string;
  executorName: string;
}

export const sendContactMessage = async (data: ContactMessageData): Promise<{ success: boolean; messageId?: string; error?: string }> => {
  try {
    const { data: result, error } = await supabase.functions.invoke('send-email', {
      body: {
        type: 'service_contact',
        to: data.executorEmail,
        data: {
          clientName: data.clientName,
          clientEmail: data.clientEmail,
          clientPhone: data.clientPhone,
          clientCompany: data.clientCompany,
          subject: data.subject,
          message: data.message,
          serviceTitle: data.serviceTitle,
          serviceId: data.serviceId,
          executorName: data.executorName
        }
      }
    });

    if (error) {
      console.error('Error sending contact message:', error);
      return { success: false, error: error.message };
    }

    return { success: true, messageId: result?.messageId };
  } catch (error) {
    console.error('Error in sendContactMessage:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

export const sendNewProjectNotification = async (
  projectId: string,
  title: string,
  description: string,
  budgetMin: number | null,
  budgetMax: number | null,
  categoryName: string,
  deadline: string | null,
  skills: string[]
): Promise<void> => {
  try {
    // Get all freelancers and agencies
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('email, full_name, company')
      .in('role', ['FREELANCER', 'AGENCY']);

    if (error) {
      console.error('Error fetching profiles for notifications:', error);
      return;
    }

    if (!profiles || profiles.length === 0) {
      console.log('No freelancers or agencies found for notifications');
      return;
    }

    // Send email to each freelancer/agency
    for (const profile of profiles) {
      try {
        await supabase.functions.invoke('send-email', {
          body: {
            type: 'new_project',
            to: profile.email,
            data: {
              projectId,
              title,
              description,
              budgetMin,
              budgetMax,
              categoryName,
              deadline,
              skills,
              recipientName: profile.full_name || profile.company || 'Freelancer'
            }
          }
        });
      } catch (emailError) {
        console.error(`Error sending email to ${profile.email}:`, emailError);
        // Continue with other emails even if one fails
      }
    }
  } catch (error) {
    console.error('Error in sendNewProjectNotification:', error);
    // Don't throw error to avoid breaking project creation
  }
};

export const sendRegistrationEmail = async (
  email: string,
  fullName: string,
  role: string
): Promise<void> => {
  try {
    await supabase.functions.invoke('send-email', {
      body: {
        type: 'registration',
        to: email,
        data: {
          fullName,
          role,
          email
        }
      }
    });
  } catch (error) {
    console.error('Error in sendRegistrationEmail:', error);
    // Don't throw error to avoid breaking registration
  }
};

export const sendAccountApprovalEmail = async (
  email: string,
  fullName: string,
  role: string
): Promise<void> => {
  try {
    await supabase.functions.invoke('send-email', {
      body: {
        type: 'account_approval',
        to: email,
        data: {
          fullName,
          role,
          email
        }
      }
    });
  } catch (error) {
    console.error('Error in sendAccountApprovalEmail:', error);
    // Don't throw error to avoid breaking admin operations
  }
};